import React, { useContext, useState, useEffect } from 'react';
import Button from 'components/Atoms/Button';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import pathOr from 'ramda.pathor';
import loadable from '@loadable/component';
import { tc } from 'utils/theme';
import { getLocalAuth, setLocalAuth } from 'utils/auth';

import { AppContext } from 'contexts/AppContext';
import SuccessIcon from 'components/Molecules/SuccessMessage/SuccessIcon';

import { verifyEmail } from 'services/authService';

const H2 = loadable(() => import('components/Atoms/H2'));
const Logo = loadable(() => import('components/Atoms/Logo'));
const Icon = loadable(() => import('components/Atoms/Icon'));
const Spinner = loadable(() => import('components/Atoms/Spinner'));
const LayoutAuth = loadable(() => import('components/Organisms/LayoutAuth'));

const Content = styled.div`
  max-width: 400px;
  margin: 0 auto;
`;

const StyledLogo = styled(Logo)`
  width: 100%;
  height: 140px;
`;

const StyledText = styled.p`
  text-align: center;
  margin: 30px 0;
`;

const StyledIcon = styled(Icon)`
  font-size: 50px;
`;

const StyledCircleCheck = styled(StyledIcon)`
  && {
    color: ${tc('circleActiveColor')};
  }
`;

const StyledCircleCross = styled(StyledIcon)`
  && {
    color: ${tc('errorMessage')};
  }
`;

const VerifyEmailAddress = () => {
  const { organizationId, propertyId } = useContext(AppContext);
  const [status, setStatus] = useState(null);
  const { verifyToken } = useParams();

  const { authUser } = getLocalAuth();
  const authUserId = pathOr(null, ['id'], authUser);

  useEffect(() => {
    const checkEmailVerification = async () => {
      const response = await verifyEmail(verifyToken);

      if (response.error) {
        return setStatus('failure');
      }

      /**
       * If user is currently authenticated, update LocalStorage
       * with updated AuthUser.
       */
      if (authUserId) {
        setLocalAuth({
          token: response.token,
          sanitizedUser: response.sanitizedUser,
        });
      }

      return setStatus('success');
    };

    checkEmailVerification();
  }, [authUserId, verifyToken]);

  const RenderLoading = () => (
    <>
      <StyledText as="div">
        <Spinner />
      </StyledText>
    </>
  );

  const RenderSuccess = () => (
    <>
      <H2 center>Thank you for verifying your email!</H2>
      <StyledText>
        <SuccessIcon />
      </StyledText>
      <StyledText>You&apos;re all set!</StyledText>
      <StyledText>
        <Button variant="primary" to={`/${organizationId}/${propertyId}`}>
          Go to&nbsp;
          {authUserId ? 'Home' : 'Login'}
        </Button>
      </StyledText>
    </>
  );

  const RenderFailure = () => (
    <>
      <H2 center>Unable to verify your email</H2>
      <StyledText>
        <StyledCircleCross iconName="et-circle-delete" />
      </StyledText>
      <StyledText>
        Make sure that you have clicked the correct link from your verification
        email.
      </StyledText>
      <StyledText>
        <Button variant="primary" to={`/${organizationId}/${propertyId}`}>
          Go to&nbsp;
          {authUserId ? 'Home' : 'Login'}
        </Button>
      </StyledText>
    </>
  );

  return (
    <LayoutAuth>
      <Content>
        <StyledLogo padBottom />
        {status === null && <RenderLoading />}
        {status === 'success' && <RenderSuccess />}
        {status === 'failure' && <RenderFailure />}
      </Content>
    </LayoutAuth>
  );
};

VerifyEmailAddress.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      verifyToken: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default VerifyEmailAddress;
